import React, { useEffect, useState } from "react";
import { commonService, toasterService } from "../../services";
import { KnobEditor } from "./KnobEditor";
import "./RatesAdmin.css";

/* Admin-only rates publisher: upload the weekly rate-sheet workbook, validate
   the "for utils" tabs, preview the diff vs what the engine is quoting today,
   then publish the dated CSVs to this box's engine. */
export function RatesAdmin() {
  const [families, setFamilies] = useState([]);
  const [file, setFile] = useState(null);
  const [effectiveDate, setEffectiveDate] = useState(() =>
    new Date().toISOString().slice(0, 10)
  );
  const [force, setForce] = useState(false);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [published, setPublished] = useState(null);
  const [engine, setEngine] = useState(null);
  const [quickResult, setQuickResult] = useState(null);
  const [abRunning, setAbRunning] = useState(false);
  const [abStatus, setAbStatus] = useState(null);
  const [relVersion, setRelVersion] = useState("");
  const [relPrev, setRelPrev] = useState("");
  const [relNotes, setRelNotes] = useState("");
  const [relSent, setRelSent] = useState(null);
  const [gsOpen, setGsOpen] = useState(false);
  const [armGrid, setArmGrid] = useState("");
  const [fixedGrid, setFixedGrid] = useState("");
  const [gsDate, setGsDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [gsForce, setGsForce] = useState(false);
  const [knobDoc, setKnobDoc] = useState(null);
  const [knobsDirty, setKnobsDirty] = useState(false);
  const [knobsRev, setKnobsRev] = useState(null);
  const [knobsInit, setKnobsInit] = useState(true);
  const [partners, setPartners] = useState(null);
  const [p620, setP620] = useState([]);
  const [pCorr, setPCorr] = useState([]);
  const [pDate, setPDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [newEntry, setNewEntry] = useState("");
  const [pTags, setPTags] = useState([]);
  const [tagInfo, setTagInfo] = useState({});
  const [pBumps, setPBumps] = useState([]);
  const [bumpInfo, setBumpInfo] = useState({});
  const [newTagSel, setNewTagSel] = useState("internal");
  const [newTagEmail, setNewTagEmail] = useState("");

  const loadStatus = () => {
    commonService
      .ratesStatus()
      .then((r) => setFamilies(Array.isArray(r.data?.families) ? r.data.families : []))
      .catch(() => toasterService.error("Couldn't load rates status."));
    commonService
      .engineStatus()
      .then((r) => setEngine(r.data))
      .catch(() => {});
  };
  useEffect(loadStatus, []);

  // Poll the long-running A/B suite while it's in flight.
  useEffect(() => {
    if (!abRunning) return undefined;
    const t = setInterval(() => {
      commonService
        .engineAbStatus()
        .then((r) => {
          setAbStatus(r.data);
          if (r.data?.done) {
            setAbRunning(false);
            loadStatus();
          }
        })
        .catch(() => {});
    }, 15000);
    return () => clearInterval(t);
  }, [abRunning]);

  const doQuick = () => {
    setBusy(true);
    setQuickResult(null);
    commonService
      .engineQuick()
      .then((r) => setQuickResult(r.data))
      .catch((e) => toasterService.error(e?.response?.data?.message || "Quick check failed."))
      .finally(() => setBusy(false));
  };

  const doSnapshot = () => {
    if (!window.confirm("Snapshot freezes the CURRENT engine code as the comparison reference. Run this BEFORE making engine changes. Continue?")) return;
    setBusy(true);
    commonService
      .engineSnapshot()
      .then((r) => {
        toasterService.success(r.data?.output || "Snapshot done.");
        loadStatus();
      })
      .catch((e) => toasterService.error(e?.response?.data?.message || "Snapshot failed."))
      .finally(() => setBusy(false));
  };

  const doAb = () => {
    setBusy(true);
    setAbStatus(null);
    commonService
      .engineAb()
      .then(() => setAbRunning(true))
      .catch((e) => toasterService.error(e?.response?.data?.message || "Couldn't start the A/B suite."))
      .finally(() => setBusy(false));
  };

  const loadKnobs = () => {
    commonService
      .ratesKnobs()
      .then((r) => {
        setKnobsInit(r.data?.initialized !== false);
        setKnobsRev(r.data?.rev ?? null);
        setKnobDoc(r.data?.knobs ?? {});
        setKnobsDirty(false);
      })
      .catch((e) =>
        toasterService.error(e?.response?.data?.message || "Couldn't load the knob file.")
      );
  };

  // family: refresh only that list's editable state, so publishing one list
  // never wipes unsaved edits in the other two. Omit (or pass anything not a
  // known family — e.g. a click event) for the initial full load.
  const loadPartners = (family) => {
    const one = family === "620" || family === "corr" || family === "tags" || family === "bumps" ? family : null;
    commonService
      .ratesPartners()
      .then((r) => {
        setPartners(r.data);
        if (!one || one === "620") setP620((r.data?.whitelist620?.entries || []).map((x) => x.entry));
        if (!one || one === "corr") setPCorr(r.data?.correspondents?.rows || []);
        if (!one || one === "tags") {
          setPTags(r.data?.ruleTags?.rows || []);
          setTagInfo(r.data?.ruleTags?.info || {});
        }
        if (!one || one === "bumps") {
          setPBumps(r.data?.rateBumps?.rows || []);
          setBumpInfo(r.data?.rateBumps?.info || {});
        }
      })
      .catch((e) =>
        toasterService.error(e?.response?.data?.message || "Couldn't load partner lists.")
      );
  };

  const openGs = () => {
    setGsOpen(true);
    if (!knobDoc) loadKnobs();
  };

  const doSaveKnobs = () => {
    if (!knobDoc) return;
    setBusy(true);
    commonService
      .ratesSaveKnobs(knobDoc, knobsRev)
      .then((r) => {
        setKnobsDirty(false);
        setKnobsInit(true);
        setKnobsRev(r.data?.rev ?? null);
        toasterService.success("Knobs saved (previous version kept in history).");
      })
      .catch((e) => toasterService.error(e?.response?.data?.message || "Save failed."))
      .finally(() => setBusy(false));
  };

  const doDerivePreview = () => {
    if (!knobDoc) {
      toasterService.error("Knobs aren't loaded yet.");
      return;
    }
    if (!armGrid.trim() || !fixedGrid.trim()) {
      toasterService.error("Paste both GSMC grids (ARM and FIXED) first.");
      return;
    }
    setBusy(true);
    setPreview(null);
    setPublished(null);
    commonService
      .ratesDerivePreview({
        armGrid,
        fixedGrid,
        effectiveDate: gsDate,
        force: gsForce,
        knobs: knobDoc,
      })
      .then((r) => setPreview(r.data))
      .catch((e) => toasterService.error(e?.response?.data?.message || "Derivation failed."))
      .finally(() => setBusy(false));
  };

  const doPublish620 = () => {
    if (!window.confirm(`Publish the 620 whitelist (${p620.length} entries) effective ${pDate}? This changes which brokers get 620-FICO program limits.`)) return;
    setBusy(true);
    commonService
      .ratesPublish620(p620, pDate, false)
      .then((r) => {
        (r.data?.warnings || []).forEach((w) => toasterService.error(w));
        toasterService.success(`Published ${r.data?.published} (${r.data?.count} entries).`);
        loadPartners("620");
      })
      .catch((e) => toasterService.error(e?.response?.data?.message || "Publish failed."))
      .finally(() => setBusy(false));
  };

  const doPublishCorr = () => {
    if (!window.confirm(`Publish the correspondent list (${pCorr.length} rows) effective ${pDate}? This changes partner pricing.`)) return;
    setBusy(true);
    commonService
      .ratesPublishCorrespondents(pCorr, pDate, false)
      .then((r) => {
        (r.data?.warnings || []).forEach((w) => toasterService.error(w));
        toasterService.success(`Published ${r.data?.published} (${r.data?.count} rows).`);
        loadPartners("corr");
      })
      .catch((e) => toasterService.error(e?.response?.data?.message || "Publish failed."))
      .finally(() => setBusy(false));
  };

  const doPublishTags = () => {
    if (!window.confirm(`Publish the engine rule tags (${pTags.length} rows) effective ${pDate}? This changes which partners get special pricing rules.`)) return;
    setBusy(true);
    commonService
      .ratesPublishTags(pTags, pDate, false)
      .then((r) => {
        (r.data?.warnings || []).forEach((w) => toasterService.error(w));
        toasterService.success(`Published ${r.data?.published} (${r.data?.count} rows).`);
        loadPartners("tags");
      })
      .catch((e) => toasterService.error(e?.response?.data?.message || "Publish failed."))
      .finally(() => setBusy(false));
  };

  const doPublishBumps = () => {
    if (!window.confirm(`Publish the engine rate adjustments (${pBumps.length} rules) effective ${pDate}? These change borrower RATES for origination dates on/after that date.`)) return;
    setBusy(true);
    commonService
      .ratesPublishBumps(pBumps, pDate, false)
      .then((r) => {
        toasterService.success(`Published ${r.data?.published} (${r.data?.count} rules).`);
        loadPartners("bumps");
      })
      .catch((e) => toasterService.error(e?.response?.data?.message || "Publish failed."))
      .finally(() => setBusy(false));
  };

  const doRollbackPartner = (family, date, label) => {
    if (!date) return;
    if (!window.confirm(`Roll back the ${label} published ${date}? The engine falls back to the previous dated file immediately.`)) return;
    setBusy(true);
    commonService
      .ratesPartnerRollback(family, date)
      .then((r) => {
        toasterService.success(`Rolled back ${r.data?.removed}.`);
        loadPartners({ utils_620fico: "620", utils_correspondent: "corr", utils_partner_tags: "tags", utils_rate_bumps: "bumps" }[family]);
      })
      .catch((e) => toasterService.error(e?.response?.data?.message || "Rollback failed."))
      .finally(() => setBusy(false));
  };

  const doRelease = () => {
    setBusy(true);
    setRelSent(null);
    commonService
      .engineReleaseEmail(relVersion, relPrev, relNotes)
      .then((r) => setRelSent(r.data))
      .catch((e) => toasterService.error(e?.response?.data?.message || "Send failed."))
      .finally(() => setBusy(false));
  };

  const doPreview = () => {
    if (!file) {
      toasterService.error("Choose the rate-sheet .xlsx first.");
      return;
    }
    const fd = new FormData();
    fd.append("workbook", file);
    fd.append("effectiveDate", effectiveDate);
    fd.append("force", String(force));
    setBusy(true);
    setPreview(null);
    setPublished(null);
    commonService
      .ratesPreview(fd)
      .then((r) => setPreview(r.data))
      .catch((e) =>
        toasterService.error(e?.response?.data?.message || "Preview failed.")
      )
      .finally(() => setBusy(false));
  };

  const doPublish = () => {
    if (!preview?.token) return;
    setBusy(true);
    commonService
      .ratesPublish(preview.token)
      .then((r) => {
        setPublished(r.data);
        setPreview(null);
        loadStatus();
      })
      .catch((e) =>
        toasterService.error(e?.response?.data?.message || "Publish failed.")
      )
      .finally(() => setBusy(false));
  };

  const doRollback = (date) => {
    setBusy(true);
    commonService
      .ratesRollback(date)
      .then((r) => {
        const n = r.data?.removed?.length || 0;
        toasterService.success(`Rolled back ${n} file(s) dated ${date}.`);
        setPublished(null);
        loadStatus();
      })
      .catch((e) =>
        toasterService.error(e?.response?.data?.message || "Rollback failed.")
      )
      .finally(() => setBusy(false));
  };

  const diffLabel = (d) => {
    if (!d) return "—";
    if (!d.currentDate) return "first file for this family";
    const shape =
      d.addedRows || d.removedRows
        ? ` · rows +${d.addedRows}/−${d.removedRows}`
        : "";
    return d.changedCells === 0
      ? `no changes vs ${d.currentDate}${shape}`
      : `${d.changedCells} cell(s) changed vs ${d.currentDate}${shape}`;
  };

  return (
    <div className="ra-wrap">
      <h4 className="ra-title">Rates publisher</h4>
      <p className="ra-sub">
        Upload the weekly rate-sheet workbook. The <code>for utils</code> tabs are
        validated and diffed against what the engine quotes today; nothing changes
        until you publish. Publishing only <b>adds</b> dated files — it never
        overwrites — so a publish can be rolled back.
      </p>

      <div className="card ra-card">
        <div className="card-body">
          <h6 className="ra-h">Engine is currently quoting from</h6>
          <table className="ra-table">
            <thead>
              <tr><th>Family</th><th>Workbook tab</th><th>Live file date</th></tr>
            </thead>
            <tbody>
              {families.map((f) => (
                <tr key={f.family}>
                  <td>{f.family}</td>
                  <td className="ra-muted">{f.tab}</td>
                  <td className="ra-num">{f.newestDate || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="card ra-card">
        <div className="card-body">
          <h6 className="ra-h">Upload &amp; preview</h6>
          <div className="ra-controls">
            <input
              type="file"
              accept=".xlsx"
              className="form-control ra-file"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
            />
            <label className="ra-datelabel">
              Effective date
              <input
                type="date"
                className="form-control"
                value={effectiveDate}
                onChange={(e) => setEffectiveDate(e.target.value)}
              />
            </label>
            <label className="ra-force">
              <input
                type="checkbox"
                checked={force}
                onChange={(e) => setForce(e.target.checked)}
              />{" "}
              allow back-dated / structural changes
            </label>
            <button className="btn btn-primary" onClick={doPreview} disabled={busy}>
              {busy ? "Working…" : "Preview"}
            </button>
          </div>
        </div>
      </div>

      {preview && (
        <div className="card ra-card">
          <div className="card-body">
            <h6 className="ra-h">
              Preview — {preview.allValid ? "all tabs valid" : "problems found"}
            </h6>
            {(preview.families || []).map((f) => (
              <div key={f.family} className={"ra-fam " + (f.valid ? "ok" : "bad")}>
                <div className="ra-fam-head">
                  <b>{f.family}</b>
                  <span className="ra-muted">{f.rows} rows</span>
                  <span className={f.valid ? "ra-ok" : "ra-bad"}>
                    {f.valid ? diffLabel(f.diff) : "invalid"}
                  </span>
                </div>
                {f.errors?.length > 0 && (
                  <ul className="ra-errors">
                    {f.errors.map((e, i) => (
                      <li key={i}>{e}</li>
                    ))}
                  </ul>
                )}
                {f.warnings?.length > 0 && (
                  <ul className="ra-warnings">
                    {f.warnings.map((w, i) => (
                      <li key={i}>⚠ {w}</li>
                    ))}
                  </ul>
                )}
                {f.valid && f.diff?.samples?.length > 0 && (
                  <table className="ra-table ra-difftable">
                    <thead>
                      <tr><th>Row</th><th>Column</th><th>Live</th><th>New</th></tr>
                    </thead>
                    <tbody>
                      {f.diff.samples.map((s, i) => (
                        <tr key={i}>
                          <td className="ra-num">{s.row}</td>
                          <td>{s.col}</td>
                          <td className="ra-num">{s.from}</td>
                          <td className="ra-num">{s.to}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            ))}
            {preview.allValid && (
              <button className="btn btn-primary ra-publish" onClick={doPublish} disabled={busy}>
                {busy ? "Publishing…" : `Publish ${preview.families?.length || 0} files dated ${preview.effectiveDate}`}
              </button>
            )}
          </div>
        </div>
      )}

      <div className="card ra-card">
        <div className="card-body">
          <h6 className="ra-h">GS-direct publish (beta)</h6>
          <p className="ra-sub">
            Skip the workbook: paste the GSMC dealer sheet, tune the knobs, and derive
            the 9 engine tables server-side (a verbatim replication of the workbook's
            math, verified to zero mismatches). Preview shows the same validation and
            diff-vs-live as a workbook upload; nothing changes until Publish.
          </p>
          {!gsOpen ? (
            <button className="btn btn-outline-primary" onClick={openGs}>
              Open GS-direct mode
            </button>
          ) : (
            <>
              <div className="ra-gsgrids">
                <label className="ra-gslabel">
                  20-yr FIXED grid (HELOAN) — paste from GSMC
                  <textarea
                    className="form-control ra-grid"
                    rows={5}
                    placeholder={"11.5\t107.86\t108.34\t...\n11.375\t..."}
                    value={fixedGrid}
                    onChange={(e) => setFixedGrid(e.target.value)}
                  />
                </label>
                <label className="ra-gslabel">
                  20-yr ARM grid (HELOC) — paste from GSMC
                  <textarea
                    className="form-control ra-grid"
                    rows={5}
                    placeholder={"11.5\t108.22\t...\n11.375\t..."}
                    value={armGrid}
                    onChange={(e) => setArmGrid(e.target.value)}
                  />
                </label>
              </div>
              <div className="ra-gslabel">
                <span>
                  Pricing knobs — your levers, in plain English{" "}
                  <button type="button" className="lo-copy" onClick={loadKnobs} disabled={busy}>
                    Reload
                  </button>
                  {knobsDirty && <span className="ra-muted"> (unsaved changes)</span>}
                  {!knobsInit && (
                    <span className="ra-bad"> Not initialized on this box — review and Save once.</span>
                  )}
                </span>
                <KnobEditor
                  doc={knobDoc}
                  onChange={(d) => {
                    setKnobDoc(d);
                    setKnobsDirty(true);
                  }}
                />
              </div>
              <div className="ra-controls">
                <label className="ra-datelabel">
                  Effective date
                  <input type="date" className="form-control" value={gsDate} onChange={(e) => setGsDate(e.target.value)} />
                </label>
                <label className="ra-force">
                  <input type="checkbox" checked={gsForce} onChange={(e) => setGsForce(e.target.checked)} /> allow back-dated / structural changes
                </label>
                <button className="btn btn-outline-secondary" onClick={doSaveKnobs} disabled={busy || !knobsDirty}>
                  Save knobs
                </button>
                <button className="btn btn-primary" onClick={doDerivePreview} disabled={busy}>
                  {busy ? "Working…" : "Derive & preview"}
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      <div className="card ra-card">
        <div className="card-body">
          <h6 className="ra-h">Partner lists</h6>
          <p className="ra-sub">
            The two email-keyed lists the engine reads: brokers on the <b>620 whitelist</b>{" "}
            get the 620-FICO program limits; <b>correspondent</b> rows drive partner
            price, adjustment and minimum loan. Matching is by substring against the
            user's <b>lowercased</b> email — mixed-case entries never match and are
            flagged. Publishing creates a new dated file (reversible).
          </p>
          {!partners ? (
            <button className="btn btn-outline-primary" onClick={() => loadPartners()} disabled={busy}>
              Load partner lists
            </button>
          ) : (
            <>
              <div className="ra-h2">
                620 whitelist <span className="ra-muted">(live: {partners.whitelist620?.date || "none"})</span>
              </div>
              <div className="ra-chips">
                {p620.map((e, i) => {
                  const dead = e !== e.toLowerCase();
                  return (
                    <span key={i} className={"ra-chip" + (dead ? " dead" : "")} title={dead ? "Mixed-case: never matches. Click the pencil to lowercase." : undefined}>
                      {e}
                      {dead && (
                        <button type="button" className="ra-chipbtn" title="Lowercase (activates matching)"
                          onClick={() => setP620(p620.map((x, j) => (j === i ? x.toLowerCase() : x)))}>✎</button>
                      )}
                      <button type="button" className="ra-chipbtn" title="Remove"
                        onClick={() => setP620(p620.filter((_, j) => j !== i))}>×</button>
                    </span>
                  );
                })}
              </div>
              <div className="ra-controls">
                <input className="form-control" style={{ maxWidth: 260 }} placeholder="new entry (e.g. broker-domain)"
                  value={newEntry} onChange={(e) => setNewEntry(e.target.value)} />
                <button className="btn btn-outline-secondary" disabled={!newEntry.trim()}
                  onClick={() => { setP620([...p620, newEntry.trim().toLowerCase()]); setNewEntry(""); }}>
                  Add
                </button>
                <label className="ra-datelabel">
                  Effective date
                  <input type="date" className="form-control" value={pDate} onChange={(e) => setPDate(e.target.value)} />
                </label>
                <button className="btn btn-primary" onClick={doPublish620} disabled={busy || p620.length === 0}>
                  Publish 620 whitelist
                </button>
                {partners.whitelist620?.date && (
                  <button className="btn btn-outline-danger" disabled={busy}
                    onClick={() => doRollbackPartner("utils_620fico", partners.whitelist620.date, "620 whitelist")}>
                    Roll back {partners.whitelist620.date}
                  </button>
                )}
              </div>

              <div className="ra-h2" style={{ marginTop: 16 }}>
                Correspondents <span className="ra-muted">(live: {partners.correspondents?.date || "none"})</span>
              </div>
              <div className="ra-scrollx">
                <table className="ra-table ra-partnertable">
                  <thead>
                    <tr><th>Email token</th><th>Type</th><th>State</th><th>Product</th><th>Loan type</th><th>Price</th><th>Adj</th><th>Min loan</th><th>CO super</th><th /></tr>
                  </thead>
                  <tbody>
                    {pCorr.map((r, i) => {
                      const setF = (k, v) => setPCorr(pCorr.map((x, j) => (j === i ? { ...x, [k]: v } : x)));
                      const bad = !/^[A-Za-z0-9@._+-]+$/.test(r.email || "") || (r.email || "").length < 4;
                      return (
                        <tr key={i} className={bad ? "ra-rowbad" : undefined}>
                          <td><input className="ra-cellin" value={r.email} title={bad ? "Not a valid email token — fix or remove this row" : undefined} onChange={(e) => setF("email", e.target.value)} /></td>
                          <td>
                            <select className="ra-cellin" value={r.type} onChange={(e) => setF("type", e.target.value)}>
                              <option>Correspondent</option><option>Broker</option>
                            </select>
                          </td>
                          <td><input className="ra-cellin" style={{ width: 52 }} value={r.state} onChange={(e) => setF("state", e.target.value.toUpperCase())} /></td>
                          <td><input className="ra-cellin" style={{ width: 52 }} value={r.product_type} onChange={(e) => setF("product_type", e.target.value)} /></td>
                          <td><input className="ra-cellin" style={{ width: 76 }} value={r.loan_type} onChange={(e) => setF("loan_type", e.target.value)} /></td>
                          <td><input className="ra-cellin" style={{ width: 70 }} value={r.price} onChange={(e) => setF("price", e.target.value)} /></td>
                          <td><input className="ra-cellin" style={{ width: 56 }} value={r.adjustment} onChange={(e) => setF("adjustment", e.target.value)} /></td>
                          <td><input className="ra-cellin" style={{ width: 76 }} value={r.min_loan} onChange={(e) => setF("min_loan", e.target.value)} /></td>
                          <td>
                            <select className="ra-cellin" value={r.CO_super} onChange={(e) => setF("CO_super", e.target.value)}>
                              <option>N</option><option>Y</option>
                            </select>
                          </td>
                          <td><button type="button" className="ra-chipbtn" onClick={() => setPCorr(pCorr.filter((_, j) => j !== i))}>×</button></td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <div className="ra-controls">
                <button className="btn btn-outline-secondary"
                  onClick={() => setPCorr([...pCorr, { email: "", type: "Correspondent", state: "ALL", product_type: "ALL", loan_type: "ALL", price: "100.5", adjustment: "0", min_loan: "50000", CO_super: "N" }])}>
                  Add row
                </button>
                <button className="btn btn-primary" onClick={doPublishCorr} disabled={busy || pCorr.length === 0}>
                  Publish correspondents
                </button>
                {partners.correspondents?.date && (
                  <button className="btn btn-outline-danger" disabled={busy}
                    onClick={() => doRollbackPartner("utils_correspondent", partners.correspondents.date, "correspondent list")}>
                    Roll back {partners.correspondents.date}
                  </button>
                )}
              </div>

              <div className="ra-h2" style={{ marginTop: 16 }}>
                Engine rule tags <span className="ra-muted">(live: {partners.ruleTags?.date || "none"})</span>
              </div>
              <p className="ra-sub">
                Each rule below used to be hardcoded to specific partner emails inside the
                engine. The behavior still lives in the engine — this list decides <b>who</b>{" "}
                gets it. Publishes are effective-dated: a change applies to runs whose
                origination date is on or after the effective date. Entries starting with{" "}
                <code>zzcanary</code> are A/B test tripwires and can't be removed.
              </p>
              {Object.keys(tagInfo).map((tag) => {
                const members = pTags.map((r, idx) => ({ r, idx })).filter((x) => x.r.tag === tag);
                const rawish = !!(tagInfo[tag].rawMatch || tagInfo[tag].partialRaw);
                return (
                  <div key={tag} className="ra-fam">
                    <div className="ra-fam-head">
                      <b>{tagInfo[tag].label}</b>
                      <span className="ra-muted">{tagInfo[tag].desc}</span>
                    </div>
                    <div className="ra-chips" style={{ marginTop: 6, marginBottom: 0 }}>
                      {members.length === 0 && <span className="ra-muted">no one</span>}
                      {members.map((m) => {
                        // Only the exact tripwire pairs the server enforces are locked.
                        const canary = (m.r.tag === "primelending" && m.r.email === "zzcanaryprime")
                          || (m.r.tag === "unlock" && m.r.email === "zzcanaryu");
                        const mixed = m.r.email !== m.r.email.toLowerCase();
                        const dead = !rawish && mixed;
                        const partial = tagInfo[tag].partialRaw && mixed;
                        return (
                          <span key={m.idx} className={"ra-chip" + (dead ? " dead" : "")}
                            title={canary ? "A/B tripwire — proves the engine is reading this file"
                              : dead ? "Mixed-case: this rule compares against the lowercased email, so this never matches. Click the pencil to lowercase."
                              : partial ? "Mixed-case: only this rule's exact-case checks will match — lowercase unless that's intended"
                              : undefined}>
                            {m.r.email}
                            {dead && (
                              <button type="button" className="ra-chipbtn" title="Lowercase (activates matching)"
                                onClick={() => setPTags(pTags.map((x, j) => (j === m.idx ? { ...x, email: x.email.toLowerCase() } : x)))}>✎</button>
                            )}
                            {!canary && (
                              <button type="button" className="ra-chipbtn" title="Remove"
                                onClick={() => setPTags(pTags.filter((_, j) => j !== m.idx))}>×</button>
                            )}
                          </span>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
              {(() => {
                // Rows whose tag isn't in the vocabulary (hand-edited file or
                // version skew) would otherwise be invisible yet block publish.
                const unknown = pTags.map((r, idx) => ({ r, idx })).filter((x) => !tagInfo[x.r.tag]);
                if (unknown.length === 0) return null;
                return (
                  <div className="ra-fam bad">
                    <div className="ra-fam-head">
                      <b>Unknown tags</b>
                      <span className="ra-muted">These rows use tags this portal doesn't know. The engine ignores them, but publishing requires removing them.</span>
                    </div>
                    <div className="ra-chips" style={{ marginTop: 6, marginBottom: 0 }}>
                      {unknown.map((m) => (
                        <span key={m.idx} className="ra-chip dead">
                          {m.r.tag}: {m.r.email}
                          <button type="button" className="ra-chipbtn" title="Remove"
                            onClick={() => setPTags(pTags.filter((_, j) => j !== m.idx))}>×</button>
                        </span>
                      ))}
                    </div>
                  </div>
                );
              })()}
              {Object.keys(tagInfo).length > 0 && (
              <div className="ra-controls">
                <select className="form-control" style={{ maxWidth: 260 }} value={newTagSel}
                  onChange={(e) => setNewTagSel(e.target.value)}>
                  {Object.keys(tagInfo).map((tag) => (
                    <option key={tag} value={tag}>{tagInfo[tag].label}</option>
                  ))}
                </select>
                <input className="form-control" style={{ maxWidth: 240 }} placeholder="email token (e.g. partnerdomain.com)"
                  value={newTagEmail} onChange={(e) => setNewTagEmail(e.target.value)} />
                <button className="btn btn-outline-secondary" disabled={!newTagEmail.trim()}
                  onClick={() => {
                    const raw = newTagEmail.trim();
                    const rawishSel = !!(tagInfo[newTagSel] && (tagInfo[newTagSel].rawMatch || tagInfo[newTagSel].partialRaw));
                    setPTags([...pTags, { tag: newTagSel, email: rawishSel ? raw : raw.toLowerCase() }]);
                    setNewTagEmail("");
                  }}>
                  Add
                </button>
                <button className="btn btn-primary" onClick={doPublishTags} disabled={busy || pTags.length === 0}>
                  Publish rule tags
                </button>
                {partners.ruleTags?.date && (
                  <button className="btn btn-outline-danger" disabled={busy}
                    onClick={() => doRollbackPartner("utils_partner_tags", partners.ruleTags.date, "engine rule tags")}>
                    Roll back {partners.ruleTags.date}
                  </button>
                )}
              </div>
              )}

              <div className="ra-h2" style={{ marginTop: 16 }}>
                Engine rate adjustments <span className="ra-muted">(live: {partners.rateBumps?.date || "none"})</span>
              </div>
              <p className="ra-sub">
                RATE adjustments that used to be hardcoded in the engine (state surcharges,
                draw-term discounts, term premiums). Values are rate points added to the
                borrower's note rate when the rule's condition applies. Publishes are
                effective-dated: history reprices with the values in force on the loan's
                origination date.
              </p>
              {pBumps.length > 0 && (
                <div className="ra-scrollx">
                  <table className="ra-table ra-partnertable">
                    <thead>
                      <tr><th>Rule</th><th>What it does</th><th>Rate points</th></tr>
                    </thead>
                    <tbody>
                      {pBumps.map((b, i) => (
                        <tr key={b.rule}>
                          <td><b>{bumpInfo[b.rule]?.label || b.rule}</b></td>
                          <td className="ra-muted">{bumpInfo[b.rule]?.desc || ""}</td>
                          <td>
                            <input className="ra-cellin" style={{ width: 90 }} value={b.value}
                              onChange={(e) => setPBumps(pBumps.map((x, j) => (j === i ? { ...x, value: e.target.value } : x)))} />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              <div className="ra-controls">
                <button className="btn btn-primary" onClick={doPublishBumps} disabled={busy || pBumps.length === 0}>
                  Publish rate adjustments
                </button>
                {partners.rateBumps?.date && (
                  <button className="btn btn-outline-danger" disabled={busy}
                    onClick={() => doRollbackPartner("utils_rate_bumps", partners.rateBumps.date, "engine rate adjustments")}>
                    Roll back {partners.rateBumps.date}
                  </button>
                )}
              </div>
            </>
          )}
        </div>
      </div>

      <div className="card ra-card">
        <div className="card-body">
          <h6 className="ra-h">Engine tests</h6>
          <p className="ra-sub">
            Run before and after engine changes. Quick = 13 real quotes vs the frozen
            baseline (~1 min). Full A/B = full scenario suite, old engine vs current (~25 min).
          </p>
          {engine && (
            <div className="ra-enginestatus">
              Baseline: {engine.baselineCases} cases
              {engine.baselineBuilt ? ` (built ${String(engine.baselineBuilt).slice(0, 10)})` : ""} ·
              Last A/B:{" "}
              {engine.ab
                ? `${engine.ab.verdict} — ${engine.ab.match}/${engine.ab.total} identical, ${engine.ab.canaryConfirmed} canary confirmed`
                : "never run"}
              {engine.abRunning ? " · A/B RUNNING" : ""}
            </div>
          )}
          <div className="ra-controls" style={{ marginTop: 10 }}>
            <button className="btn btn-primary" onClick={doQuick} disabled={busy || abRunning}>
              Run quick check
            </button>
            <button className="btn btn-outline-primary" onClick={doAb} disabled={busy || abRunning}>
              Run full A/B (~25 min)
            </button>
            <button className="btn btn-outline-secondary" onClick={doSnapshot} disabled={busy || abRunning}>
              Snapshot engine (pre-change)
            </button>
          </div>
          {quickResult && (
            <div className={"ra-testresult " + (quickResult.pass ? "ok" : "bad")}>
              {quickResult.pass ? "✅ ZERO_DIFF — engine output identical to baseline." : "❌ Differences found:"}
              {!quickResult.pass && <pre className="ra-pre">{quickResult.output}</pre>}
            </div>
          )}
          {(abRunning || abStatus) && (
            <div className={"ra-testresult " + (abStatus?.done ? (abStatus?.verdict === "PASS" ? "ok" : "bad") : "")}>
              {abStatus?.done
                ? `${abStatus.verdict === "PASS" ? "✅" : "❌"} A/B ${abStatus.verdict}${
                    abStatus.summary
                      ? ` — ${abStatus.summary.match}/${abStatus.summary.total} identical, ${abStatus.summary.unexpected} unexpected difference(s).`
                      : " — report unavailable (check ab_run.log on the box)."
                  }`
                : `Running… ${abStatus?.progress || 0} scenarios complete.`}
            </div>
          )}
        </div>
      </div>

      <div className="card ra-card">
        <div className="card-body">
          <h6 className="ra-h">Release announcement</h6>
          <p className="ra-sub">
            Sends a release email to <b>lending@buttonfinance.com</b> with what changed
            and fresh verification results (the quick check runs automatically before sending).
            One change per line; "Title: detail" renders the title in bold.
          </p>
          <div className="ra-controls">
            <label className="ra-datelabel">
              Version
              <input className="form-control" style={{ maxWidth: 140 }} placeholder="v2" value={relVersion} onChange={(e) => setRelVersion(e.target.value)} />
            </label>
            <label className="ra-datelabel">
              Previous
              <input className="form-control" style={{ maxWidth: 140 }} placeholder="v1" value={relPrev} onChange={(e) => setRelPrev(e.target.value)} />
            </label>
          </div>
          <textarea
            className="form-control ra-notes"
            rows={5}
            placeholder={"Rates publisher: admins upload the weekly workbook...\nEngine hygiene: quotes ~2x faster..."}
            value={relNotes}
            onChange={(e) => setRelNotes(e.target.value)}
          />
          <button className="btn btn-primary" onClick={doRelease} disabled={busy || !relVersion.trim() || !relNotes.trim()}>
            {busy ? "Working…" : "Run checks & send release email"}
          </button>
          {relSent && (
            <div className="ra-testresult ok">
              ✅ Sent to {relSent.to} — golden tape {relSent.quickPass ? "PASS" : "FAIL"}, A/B {relSent.abVerdict || "n/a"}.
            </div>
          )}
        </div>
      </div>

      {published && (
        <div className="card ra-card ra-published">
          <div className="card-body">
            <h6 className="ra-h">Published ✓</h6>
            <p className="ra-sub">
              {published.published?.length} files dated {published.effectiveDate} are
              live. New quotes pick them up automatically — no restart needed.
            </p>
            <ul className="ra-filelist">
              {(published.published || []).map((n) => (
                <li key={n}>{n}</li>
              ))}
            </ul>
            <button
              className="btn btn-outline-danger btn-sm"
              onClick={() => doRollback(published.effectiveDate)}
              disabled={busy}
            >
              Undo this publish
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default RatesAdmin;
